import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Button from "../../../../../components/Button/Button";
import Input from "../../../../../components/Input/Input";
import Select from "../../../../../components/Select/Select";
import Modal from "../../../../../components/Modal/Modal";
import "./PersonalData.css";
// utils
import { formatDate } from "../../../../../utils/utils";
//services
import { usersList } from "../../../../../services/user";
import { setCustomerName } from "../../../../../store/slices/customerSlice";

// Options for selects
const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const PersonalData = ({ customerData }) => {
  const dispatch = useDispatch();
  const [edit, setEdit] = useState(true);
  const [formData, setFormData] = useState(customerData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitle] = useState({
    text: "Successful",
    highlight: "change",
  });
  const [message, setMessage] = useState("User data updated successfully");
  const [error, setError] = useState(false);
  const [formErrors, setFormErrors] = useState({
    name: "",
    telephone: "",
    gender: "",
  });

  const getGenderLabel = (value) => {
    const option = genderOptions.find(
      (opt) => opt.value === value?.toLowerCase(),
    );
    return option ? option.label : value;
  };

  const getStatusLabel = (value) => {
    const option = statusOptions.find(
      (opt) => opt.value === value?.toLowerCase(),
    );
    return option ? option.label : value;
  };

  // Actualizar formData cuando customerData cambie
  const handleCancel = () => {
    setFormData(customerData); // Restaurar datos originales
    setFormErrors({
      name: "",
      telephone: "",
      gender: "",
    }); // Limpiar errores
    setEdit(true); // Volver al modo vista
  };

  useEffect(() => {
    setFormData(customerData);
  }, [customerData]);

  const validateForm = () => {
    let isValid = true;
    const errors = {
      name: "",
      telephone: "",
      gender: "",
    };

    // Validación del nombre
    if (!formData.name || formData.name.length < 2) {
      errors.name = "Name must be at least 2 characters long";
      isValid = false;
    } else if (formData.name.length > 250) {
      errors.name = "Name must not exceed 250 characters";
      isValid = false;
    }

    // Validación del teléfono
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.telephone) {
      errors.telephone = "Phone number is required";
      isValid = false;
    } else if (!phoneRegex.test(formData.telephone)) {
      errors.telephone = "Phone number must be exactly 10 digits";
      isValid = false;
    }

    // Validación del género
    if (!formData.gender) {
      errors.gender = "Please select a gender";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar el error del campo cuando se modifica
    setFormErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setTitle({ text: "Error", highlight: "validation" });
      setMessage("Please check the form for errors");
      setError(true);
      setIsModalOpen(true);
      return;
    }

    try {
      const data = {
        name: formData.name,
        profile: {
          birthdate: formData.dateOfBirth,
          gender: formData.gender,
        },
      };
      const response = await usersList.updateProfile(data, customerData.id);
      if (response.status === "success") {
        const dataUser = {
          id: customerData.id,
          name: formData.name,
          phone: formData.telephone,
        };
        const responseUser = await usersList.updateUser(dataUser);
        if (responseUser.status === "success") {
          dispatch(setCustomerName(formData.name));
          setEdit(true);
          setTitle({ text: "Successful", highlight: "change" });
          setMessage("Successfully updated");
          setError(false);
          setIsModalOpen(true);
        } else {
          setTitle({ text: "Error", highlight: "update" });
          setMessage("Failed to update");
          setError(true);
          setIsModalOpen(true);
        }
      } else {
        setTitle({ text: "Error", highlight: "update" });
        setMessage("Failed to update");
        setError(true);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  return (
    <>
      <div className="info-grid">
        <div className="info-group">
          <div className="info-label">Client's name</div>
          <Input
            type="text"
            value={formData.name || ""}
            onChange={(e) => handleInputChange("name", e.target.value)}
            disabled={edit}
            error={formErrors.name}
          />
        </div>
        <div className="info-group">
          <div className="info-label">E-mail</div>
          <Input
            type="email"
            value={formData.email || ""}
            onChange={(e) => handleInputChange("email", e.target.value)}
            disabled={true}
          />
        </div>
        <div className="info-group">
          <div className="info-label">Client qualification</div>
          <Input
            type="text"
            value={formData.profile?.customer_rating}
            onChange={(e) =>
              handleInputChange("customer_rating", e.target.value)
            }
            disabled={true}
          />
        </div>
        <div className="info-group">
          <div className="info-label">Supplier qualification</div>
          <Input
            type="text"
            value={formData.profile?.supplier_rating}
            onChange={(e) =>
              handleInputChange("supplier_rating", e.target.value)
            }
            disabled={true}
          />
        </div>
        <div className="info-group">
          <div className="info-label">Telephone</div>
          <Input
            type="tel"
            value={formData.telephone || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "");
              if (value.length <= 10) {
                handleInputChange("telephone", value);
              }
            }}
            disabled={edit}
            error={formErrors.telephone}
          />
        </div>
        <div className="info-group">
          <div className="info-label">Date of birth</div>
          {edit ? (
            <Input
              type="text"
              value={formatDate(formData.profile?.birthdate) || ""}
              disabled={true}
            />
          ) : (
            <DatePicker
              selected={
                formData.profile?.birthdate
                  ? new Date(formData.profile?.birthdate)
                  : null
              }
              onChange={(date) => handleInputChange("dateOfBirth", date)}
              dateFormat="yyyy-MM-dd"
              className="custom-datepicker"
              placeholderText="Select date"
              maxDate={new Date()}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
            />
          )}
        </div>
        <div className="info-group">
          <div className="info-label">Gender</div>
          {edit ? (
            <Input
              type="text"
              value={getGenderLabel(formData.profile?.gender) || ""}
              disabled={true}
            />
          ) : (
            <Select
              options={genderOptions}
              value={formData.profile?.gender || ""}
              onChange={(e) => handleInputChange("gender", e.target.value)}
              placeholder="Select gender"
              error={formErrors.gender}
            />
          )}
        </div>
        <div className="info-group">
          <div className="info-label">Status</div>
          <Input
            type="text"
            value={getStatusLabel(formData.status) || ""}
            disabled={true}
          />
        </div>
        <div className="info-group">
          <div className="info-label">Location</div>
          <Input
            type="text"
            value={formData.location || ""}
            onChange={(e) => handleInputChange("location", e.target.value)}
            disabled={true}
          />
        </div>
        <div className="info-group">
          <div className="info-label">Date of registration</div>
          <Input
            type="text"
            value={formatDate(formData.dateOfRegistration) || ""}
            disabled={true}
          />
        </div>
      </div>
      <h3>Payment Methods</h3>
      <div className="info-grid">
        {formData.paymentMethods.map(paymentMethod => (
          <div className="info-group">
            <p>Brand: {paymentMethod.brand}</p>
            <p>Last 4 digits: {paymentMethod.last4}</p>
            <p>Exp: {paymentMethod.exp_month} - {paymentMethod.exp_year}</p>
          </div>
        ))}
      </div>

      {!edit && (
        <div className="actions-container">
          <Button
            onClick={() => setEdit(!edit)}
            variant="primary"
            borderRadius={20}
            fullWidth
          >
            Edit
          </Button>
        </div>
      )}
      {!edit && (
        <div className="save-container">
          <Button
            onClick={handleCancel}
            variant="warning"
            borderRadius={20}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            borderRadius={20}
            fullWidth
          >
            Save
          </Button>
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        error={error}
        title={title}
        message={message}
        onConfirm={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default PersonalData;
